import { collect, Collection } from 'collect.js';
import { SourceItem } from '@/components/AccessPicker/types';
import { EventEmitter } from '../providers';
import type Router from '@/router';

export class NodeModel extends EventEmitter {
  public id!: string;

  public type!: string;

  public routeName = '';

  public intro = false;

  public indicator? = false;

  public collection: Collection<any> = collect();

  public children!: any[];

  public active = false;

  public animatingLeave = false;

  public animatingEnter = false;

  public isExpanded? = false;

  public skipExpanding? = false;

  public provider: any;

  public element: HTMLElement | null = null;

  public parent?: any;

  public isChild? = false;

  public groupId?: number;

  public visualGroupId?: number;

  public sameVisualGroup?: boolean;

  public key!: string;

  public value: SourceItem | null = null;

  public invalid?: boolean;

  public prev?: boolean;

  public component = {
    module: '',
    props: {
      image: '',
      pedigree: {
        type: '',
        path: '',
        description: '',
      },
      question: '',
      flowIndex: -1,
      isMandatory: undefined,
      title: '',
      subtitle: '',
      intro: false,
    },
    serverTask: '',
  };

  // calculated questionTitle for userFlowHistory
  public questionTitle?: string;

  protected _enabled?: boolean = undefined;

  public constructor(props: unknown, provider: any) {
    super();
    Object.assign(this, props);

    this.provider = provider;
  }

  public get isExpandable(): boolean {
    return this.collection.isNotEmpty();
  }

  public get enabled() {
    if (typeof this._enabled !== 'undefined') { return this._enabled; }

    return true;
  }

  public get flatCollection() {
    return this.provider.collection
      .flatMap((node: any) => (node.collection.isNotEmpty() ? [node, ...node.collection.all()] : [node])) as unknown as Collection<NodeModel>;
  }

  public get lastInCollection() {
    return this.provider.collection.last().key === this.key;
  }

  public get firstInCollection() {
    return this.provider.collection.first().key === this.key;
  }

  public toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  public setElement(element: HTMLElement) {
    this.element = element;
  }

  public navigate(forward = true, isDirectNavigation = false): void {
    throw new Error('Navigate method is not implemented.');
  }

  public sameVisualGroupAs(node: NodeModel) {
    return this.visualGroupId && this.visualGroupId === node.visualGroupId;
  }

  public isBefore(node: NodeModel | string) {
    const predicateName = typeof node === 'string' ? node : node.routeName;
    const currentIndex = this.flatCollection.search(n => n.routeName === this.routeName, true);
    const nodeIndex = this.flatCollection.search(n => n.routeName === predicateName, true);

    return currentIndex < nodeIndex;
  }

  public isBeforeOrSame(node: NodeModel | string) {
    const predicateName = typeof node === 'string' ? node : node.routeName;

    return this.routeName === predicateName || this.isBefore(node);
  }

  public deactivate() {
    this.active = false;
  }

  public async push(router: typeof Router) {
    const fullPath = this.parent ? `/${this.parent.routeName}/${this.routeName}` : `/${this.routeName}`;

    if (router.currentRoute.value.name !== fullPath && router.currentRoute.value.fullPath !== fullPath) {
      return router.replace(fullPath);
    }

    return router.currentRoute;
  }
}
